import { FC, useMemo } from "react";
import { Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next"; // Import useTranslation

import { GetTasterExperimentSummaryResponse } from "api/client";

import { useWindowSize } from "hooks/window-size";

import { Button } from "components/common/Button";
import { Loader } from "components/common/Loader";
import { Tooltip } from "components/common/Tooltip";

import {
  StyledRunningExperiment,
  StyledRunningExperimentDivider,
  StyledRunningExperimentInner,
  StyledRunningExperimentItem,
} from "styles/features/home";

interface RunningExperimentProps {
  experiment: GetTasterExperimentSummaryResponse;
  name?: string;
  location?: string;
  date?: string;
  time?: string;
  loading?: boolean;
  submitting?: boolean;
  disabled?: boolean;

  onClick?: (experimentId: string) => void;
}

const RunningExperiment: FC<RunningExperimentProps> = ({
  experiment,
  loading,
  disabled,
  submitting,
  onClick,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(); // Initialize translation
  const { windowSize } = useWindowSize();

  const { id, name, schedule } = experiment;
  const { startTime, location } = schedule;

  const renderedDate = useMemo(() => {
    if (startTime) {
      const date = new Date(startTime as string).toLocaleDateString();
      return date !== "Invalid Date" ? date : "-";
    }
    return "-";
  }, [startTime]);

  const renderedTime = useMemo(() => {
    if (startTime) {
      const time = new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "numeric",
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).format(new Date(startTime as string));
      return time !== "Invalid Date" ? time : "-";
    }
    return "-";
  }, [startTime]);

  return loading ? (
    <StyledRunningExperiment className="loading">
      <Loader width="50px" height="50px" />
    </StyledRunningExperiment>
  ) : (
    <StyledRunningExperiment>
      <StyledRunningExperimentInner>
        <StyledRunningExperimentItem>
          <Typography variant="bodyS" color={theme.palette.customGrey.dark}>
            {t('experiment_name')} 
          </Typography>
          <Typography
            variant="bodyL"
            color={theme.palette.common.black}
            fontWeight={500}
          >
            {name || "-"}
          </Typography>
        </StyledRunningExperimentItem>
        <StyledRunningExperimentDivider />
        <StyledRunningExperimentItem>
          <Typography variant="bodyS" color={theme.palette.customGrey.dark}>
            {t('location')} 
          </Typography>
          <Typography
            variant="bodyL"
            color={theme.palette.common.black}
            fontWeight={300}
          >
            {location || "-"}
          </Typography>
        </StyledRunningExperimentItem>
        <StyledRunningExperimentDivider />
        <StyledRunningExperimentItem>
          <Typography variant="bodyS" color={theme.palette.customGrey.dark}>
            {t('date')} 
          </Typography>
          <Typography
            variant="bodyL"
            color={theme.palette.common.black}
            fontWeight={300}
          >
            {renderedDate || "-"}
          </Typography>
        </StyledRunningExperimentItem>
        <StyledRunningExperimentDivider />
        <StyledRunningExperimentItem>
          <Typography variant="bodyS" color={theme.palette.customGrey.dark}>
            {t('time')} 
          </Typography>
          <Typography
            variant="bodyL"
            color={theme.palette.common.black}
            fontWeight={300}
          >
            {renderedTime || "-"}
          </Typography>
        </StyledRunningExperimentItem>
      </StyledRunningExperimentInner>
      <Tooltip
        text={disabled ? t('please_wait') : undefined} 
        wrapClassName="running-experiment-tooltip"
        size="auto"
        position={windowSize.width > 1199 ? "bottom-right" : "bottom-left"}
      >
        <Button
          size="md"
          className="join-btn"
          disabled={disabled || submitting}
          isLoading={submitting}
          onClick={() => onClick?.(id)}
        >
          {t('join_experiment')} 
        </Button>
      </Tooltip>
    </StyledRunningExperiment>
  );
};

export default RunningExperiment;
